import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Infinity as InfinityIcon, CalendarClock, Wallet as WalletIcon, CreditCard } from "lucide-react";

const euro = (c) => `€${(c / 100).toFixed(2)}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export function SearchQuotaLine({ quota }) {
  const { remaining, free_limit: limit, resets_at: resetsAt, credits } = quota;
  return (
    <div data-testid="ai-search-quota" className="px-5 -mt-2 pb-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#9CA3AF]">
      <span data-testid="ai-search-free-left" className="flex items-center gap-1">
        <Sparkles className="h-3 w-3 text-[#3B82F6]" />
        <b className="text-[#F3F4F6] tabular-nums">{remaining}</b> of {limit} free left this week
      </span>
      <span data-testid="ai-search-credits-left" className="flex items-center gap-1">
        <InfinityIcon className="h-3 w-3 text-[#22C55E]" />
        <b className="text-[#F3F4F6] tabular-nums">{credits}</b> purchased left
      </span>
      <span data-testid="ai-search-reset-date" className="flex items-center gap-1">
        <CalendarClock className="h-3 w-3 text-[#6B7280]" />
        {resetsAt ? <>Free resets on <b className="text-[#F3F4F6]">{fmtDate(resetsAt)}</b></> : "Free resets 7 days after your first search"}
      </span>
    </div>
  );
}

export function SearchPacks({ packs, onPick, busy, exhausted }) {
  return (
    <div data-testid="ai-search-packs" className={`px-5 py-5 space-y-3 ${exhausted ? "" : "border-t border-[#27272A]"}`}>
      <div>
        <div className="text-sm font-semibold text-[#F3F4F6]">{exhausted ? "You've used your free searches" : "Need more searches?"}</div>
        <div className="text-xs text-[#9CA3AF]">Pay from your wallet or by card. Purchased searches never expire.</div>
      </div>
      <div className={`grid gap-2 ${packs.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {packs.map((p) => (
          <button
            key={p.id}
            type="button"
            disabled={busy}
            data-testid={`ai-search-pack-${p.id}`}
            onClick={() => onPick(p)}
            className="text-left rounded-xl border border-[#27272A] bg-[#1E1E1E] hover:border-[#3B82F6] hover:bg-[#27272A] px-3.5 py-3 transition-colors disabled:opacity-50"
          >
            <div className="text-xs text-[#9CA3AF]">{p.name}</div>
            <div className="text-sm font-semibold text-[#F3F4F6]">
              {p.searches} {p.searches === 1 ? "search" : "searches"}
            </div>
            <div className="text-sm text-[#60A5FA] tabular-nums">{euro(p.price_cents)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Shell({ open, onCancel, testid, title, body, action, onAction }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid={testid} className="bg-[#121212] border-[#27272A] text-[#F3F4F6] w-[calc(100%-2rem)] max-w-sm rounded-2xl p-5">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle className="font-heading text-lg">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[#9CA3AF]">{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <AlertDialogCancel data-testid={`${testid}-cancel-btn`} className="mt-0 h-10 rounded-full bg-transparent border-[#27272A] text-[#9CA3AF] hover:bg-[#1E1E1E] hover:text-[#F3F4F6] focus-visible:ring-0 focus-visible:ring-offset-0">
            Cancel
          </AlertDialogCancel>
          <Button data-testid={`${testid}-confirm-btn`} onClick={onAction} className="h-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
            {action}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function BuyPackDialog({ pack, runsSearch, onCancel, onConfirm, onCard, balance }) {
  const short = pack && balance != null && balance < pack.price_cents;
  return (
    <AlertDialog open={!!pack} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="search-buy-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] w-[calc(100%-2rem)] max-w-sm rounded-2xl p-5">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle className="font-heading text-lg">{pack ? `Buy ${pack.name}?` : ""}</AlertDialogTitle>
          <AlertDialogDescription className="text-[#9CA3AF]">
            {pack ? `${pack.searches} ${pack.searches === 1 ? "search" : "searches"} for ${euro(pack.price_cents)}. Choose how to pay.${runsSearch ? " With wallet, your search runs right after." : ""}` : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {balance != null && <div data-testid="search-buy-wallet-balance" className="text-xs text-[#9CA3AF]">Wallet balance: <b className={short ? "text-[#F87171]" : "text-[#F3F4F6]"}>{euro(balance)}</b></div>}
        <div className="space-y-2 pt-1">
          <Button data-testid="search-buy-dialog-confirm-btn" onClick={onConfirm} disabled={short}
            className="w-full h-10 rounded-full bg-[#10B981] hover:bg-[#059669] text-white flex items-center gap-2 disabled:opacity-40">
            <WalletIcon className="h-4 w-4" /> {short ? "Not enough in wallet" : `Pay ${pack ? euro(pack.price_cents) : ""} from wallet`}
          </Button>
          <Button data-testid="search-buy-dialog-card-btn" onClick={onCard}
            className="w-full h-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Pay with card
          </Button>
          <AlertDialogCancel data-testid="search-buy-dialog-cancel-btn" className="mt-0 w-full h-10 rounded-full bg-transparent border-[#27272A] text-[#9CA3AF] hover:bg-[#1E1E1E] hover:text-[#F3F4F6] focus-visible:ring-0 focus-visible:ring-offset-0">
            Cancel
          </AlertDialogCancel>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function NoBalanceDialog({ open, onCancel, onTopUp }) {
  return (
    <Shell
      open={open}
      onCancel={onCancel}
      testid="search-charge-dialog"
      title="Not enough balance"
      body="Your wallet doesn't have enough for this option. Top up your wallet to keep searching."
      action="Top up"
      onAction={onTopUp}
    />
  );
}
